import { AbstractStoreService } from '@abstract-services/store/1.0/store.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreAPI } from '@constants/api';
import { AggregatorConfig } from '@interfaces/aggregator/1.0';
import { MenuJob } from '@interfaces/menu-job/1.0';
import { ImageItem, NearbyStoresParams, Store, StoreView } from '@interfaces/store/1.0';
import { PaginationService } from '@services/pagination/1.0/pagination.service';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, reduce, shareReplay, tap } from 'rxjs/operators';
import { StoresService as GeneratedStoresService } from '@generated/tcea/services/stores.service';
import { PriceOverrides } from '@generated/tcea/models/price-overrides';
import { MarkupOverride } from '@generated/tcea/models/markup-override';
import { ProductsService as GeneratedProductsService } from '@generated/tcea/services/products.service';
import { BulkUpdateCatalogEntryOverrides$Params } from '@generated/tcea/fn/products/bulk-update-catalog-entry-overrides';
import { StoreViewResponse } from '@interfaces/store/1.0/index';
import { DoordashService } from '@generated/tcea/services/doordash.service';
import { AvailabilityService } from '@generated/tcea/services/availability.service';
import { ChannelsBulkJobsRequest } from '@generated/tcea/models/channels-bulk-jobs-request';
import { OutOfStockUpdateInput } from '@generated/tcea/models/out-of-stock-update-input';
import { ChannelsBulkJobsService } from '@generated/tcea/services/channels-bulk-jobs.service';
import { OutOfStock$Params } from '@generated/tcea/fn/availability/out-of-stock';
import { OutOfStockBulk$Params } from '@generated/tcea/fn/availability/out-of-stock-bulk';
import {
  AggregatorSummaryResponse,
  BulkStoreUpdateResponse,
  BulkStoresAmenitiesDetailsResponse,
  BulkStoresAmenitiesRequest,
  BulkStoresAmenitiesSummaryResponse,
  DoordashOnboardingValidationResponse,
  StoreAggregatorOnboarding,
  StoreAggregatorOnboardingGetResponse,
  StoreAmenityCollectionResponse,
  StoreAmenityResponse,
  StoreOrderingDeactivation,
  StoreOrderingDeactivationsResponse,
  StoreOrderingStatusRequest,
  SignupUrlRequest,
  SignupUrlResponse,
  StoresGetResponse,
  UpdateStoreAmenitiesRequest,
  UpdateStoreAmenitiesResponse,
} from '@generated/tcea/models';
import { StoreAggregatorOnboardingService } from '@generated/tcea/services/store-aggregator-onboarding.service';
import { StoresStoreIdCatalogEntryOverridesCatalogEntryIdChannelIdDelete$Params } from '@generated/tcea/fn/stores/stores-store-id-catalog-entry-overrides-catalog-entry-id-channel-id-delete';
import { StoreAggregatorOnboarding$Params } from '@generated/tcea/fn/store-aggregator-onboarding/store-aggregator-onboarding';
import { DoordashStoresStoreIdOnboardingValidationGet$Params } from '@generated/tcea/fn/doordash/doordash-stores-store-id-onboarding-validation-get';
import { StoresStoreIdOnboardingAggregatorsAggregatorIdPut$Params } from '@generated/tcea/fn/store-aggregator-onboarding/stores-store-id-onboarding-aggregators-aggregator-id-put';
import { GetStoreOrderingDeactivations$Params } from '@generated/tcea/fn/stores/get-store-ordering-deactivations';
import { AggregatorsSummaryGet$Params } from '@generated/tcea/fn/stores/aggregators-summary-get';
import { UpdateStoreOrderingStatus$Params } from '@generated/tcea/fn/stores/update-store-ordering-status';
import { AggregatorsDoordashStoresGet$Params } from '@generated/tcea/fn/doordash/aggregators-doordash-stores-get';
import { AggregatorsDoordashSignupUrlPost$Params } from '@generated/tcea/fn/doordash/aggregators-doordash-signup-url-post';
import { GetStoreAmenities$Params } from '@generated/tcea/fn/stores/get-store-amenities';
import { PutStoreAmenities$Params } from '@generated/tcea/fn/stores/put-store-amenities';
import { BulkStoreAmenitiesGetAll$Params } from '@generated/tcea/fn/stores/bulk-store-amenities-get-all';
import { BulkStoreAmenitiesGetDetails$Params } from '@generated/tcea/fn/stores/bulk-store-amenities-get-details';
import { BulkStoresAmenities$Params } from '@generated/tcea/fn/stores/bulk-stores-amenities';
import { DaypartsService } from '@generated/tcea/services/dayparts.service';
import { DaypartsDaypartIdStoresStoreIdGet$Params } from '@generated/tcea/fn/dayparts/dayparts-daypart-id-stores-store-id-get';
import { DaypartsDaypartIdStoresStoreIdPut$Params } from '@generated/tcea/fn/dayparts/dayparts-daypart-id-stores-store-id-put';
import { DaypartsDaypartIdMenusMenuIdGet$Params } from '@generated/tcea/fn/dayparts/dayparts-daypart-id-menus-menu-id-get';
import { DaypartsDaypartIdMenusMenuIdPut$Params } from '@generated/tcea/fn/dayparts/dayparts-daypart-id-menus-menu-id-put';
import { DayPartMap } from '@interfaces/dayParts/1.0';
import { WeekHours } from '@generated/tcea/models';

@Injectable({
  providedIn: 'root',
})
export class StoreService extends AbstractStoreService {
  storeApiUrl: string;
  private readonly storesCache = new Map<string, StoreView[]>();
  private readonly storesInFlight = new Map<string, Observable<StoreView[]>>();

  private getStoresCacheKey(brandId: string, fields: string[]): string {
    // Normalize fields so semantically identical requests share the same cache entry.
    const normalizedFields = [...fields].sort();
    return `${brandId}|${normalizedFields.join(',')}`;
  }

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
    private generatedStoresService: GeneratedStoresService,
    private storeAggregatorOnboardingService: StoreAggregatorOnboardingService,
    private generatedProductsService: GeneratedProductsService,
    private doordashService: DoordashService,
    private availabilityService: AvailabilityService,
    private daypartsService: DaypartsService,
    private channelsBulkJobsService: ChannelsBulkJobsService,
  ) {
    super();
    this.storeApiUrl = StoreAPI.GET;
  }

  /**
   * Results are cached per brandId + fields combination
   *
   * @param brandId [string]
   * @param fields [string[]] optional field filter
   * @param pageSize [number] optional page size for pagination (default: 500)
   * @returns observable of all stores (cached)
   */
  get(brandId: string, fields: string[] = [], pageSize: number = 500): Observable<StoreView[]> {
    const cacheKey = this.getStoresCacheKey(brandId, fields);
    const cached = this.storesCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const inFlight = this.storesInFlight.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const headers = new HttpHeaders({ 'active-tenant-identifier': brandId });
    const params: Record<string, string | string[]> = {};

    if (fields.length) {
      params.field = fields;
    }

    const request$ = this.paginationService
      .fetchAllPages(this.storeApiUrl, { headers, params, pageSize })
      .pipe(
        map((response: StoreViewResponse) => {
          if (response && response.results) {
            return response.results as unknown as StoreView[];
          }
          return [] as StoreView[];
        }),
        reduce((acc: StoreView[], pageResults: StoreView[]) => acc.concat(pageResults), []),
        tap((stores: StoreView[]) => {
          this.storesCache.set(cacheKey, stores);
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
        finalize(() => {
          this.storesInFlight.delete(cacheKey);
        }),
        shareReplay(1),
      );

    this.storesInFlight.set(cacheKey, request$);

    return request$;
  }

  /**
   * Gets export data for the brand in csv format
   * required headers are set in add-jwt-header interceptor
   */
  getExportData(queryString: string): Observable<any> {
    return this.http
      .get(`${this.storeApiUrl}export?` + queryString, { responseType: 'text' as 'json' })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all stores based on the filters provided
   * for the current brand context
   *
   * @param brandId string
   * @param [filterParams] string
   * @param returnResponse - if true, function returns the response object
   * @returns observable of all stores, or pagination response
   */
  getAllByFilters(
    brandId: string,
    filterParams: string = '',
    returnResponse = false,
  ): Observable<StoreView[] | StoreViewResponse> {
    const headers = new HttpHeaders({ 'active-tenant-identifier': brandId });
    const URL = `${this.storeApiUrl}${filterParams}`;

    return this.paginationService.fetchAllPages(URL, { headers }).pipe(
      map((response: StoreViewResponse) => {
        if (response && response.results) {
          if (returnResponse) {
            return response;
          }

          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all stores for current brand context
   *
   * @param brandId string
   * @returns observable of all stores
   */
  getAll(brandId: string): Observable<StoreView[] | StoreViewResponse> {
    return this.getAllByFilters(brandId);
  }

  /**
   * Gets the current page provided
   *
   * @param brandId string
   * @param filterParams string
   * @returns observable of all stores
   */
  getPage(brandId: string, filterParams: string = ''): Observable<StoreViewResponse> {
    const headers = new HttpHeaders({ 'active-tenant-identifier': brandId });
    const URL = `${this.storeApiUrl}${filterParams}`;

    return this.paginationService.fetchPage(URL, { headers });
  }

  /**
   * post bulk updates to selected list of stores
   *
   * @param body
   * @returns observable of all bulkEdit response
   */
  postBulkUpdates(body): Observable<StoreView[]> {
    return this.http.post(`${this.storeApiUrl}bulk-updates`, body).pipe(
      map((response: { store: StoreView[] }) => {
        if (response && response.store) {
          const RESULT = response.store;
          return RESULT;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all stores light weight response to display in store grid
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  getBulkUpdates(params): Observable<BulkStoreUpdateResponse[]> {
    let url = params
      ? `${this.storeApiUrl}bulk-updates${params}`
      : `${this.storeApiUrl}bulk-updates`;
    return this.http.get(url).pipe(
      map((response: StoreViewResponse) => {
        if (response && response?._embedded?.bulkStoreUpdateRequests) {
          return response?._embedded?.bulkStoreUpdateRequests;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all stores light weight response to display in store grid
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  getBulkUpdateDetailsById(id): Observable<StoreView[]> {
    return this.http.get(`${this.storeApiUrl}bulk-updates/${id}/details`).pipe(
      map((response: StoreViewResponse) => {
        if (response && response?._embedded?.stores) {
          return response?._embedded?.stores;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getBulkStoreAmenitiesSummaries(
    brandId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      status?: 'IN_PROGRESS' | 'SUCCESS' | 'ERROR';
    },
  ): Observable<BulkStoresAmenitiesSummaryResponse[]> {
    const params: BulkStoreAmenitiesGetAll$Params = {
      'Active-Tenant-Identifier': brandId,
      startDate: options?.startDate,
      endDate: options?.endDate,
      status: options?.status,
    };
    return this.generatedStoresService.bulkStoreAmenitiesGetAll(params).pipe(
      map((response) => {
        const embedded = response?._embedded as {
          bulkRequests?: BulkStoresAmenitiesSummaryResponse[];
          catalogChanges?: BulkStoresAmenitiesSummaryResponse[];
        };
        return embedded?.bulkRequests ?? embedded?.catalogChanges ?? [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getBulkStoreAmenitiesDetailsById(
    bulkRequestId: string,
    brandId: string,
  ): Observable<BulkStoresAmenitiesDetailsResponse> {
    const params: BulkStoreAmenitiesGetDetails$Params = {
      'Active-Tenant-Identifier': brandId,
      bulkRequestId,
    };
    return this.generatedStoresService
      .bulkStoreAmenitiesGetDetails(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Gets one store
   *
   * @param storeId [string]
   * @returns observable of all stores
   */
  getOne(storeId: string, headers?): Observable<Store> {
    return this.http.get(`${this.storeApiUrl}${storeId}`, { headers }).pipe(
      map((response: Store) => {
        // TODO: removing these elements from store model as these items will be deprecated in upcoming sprints.
        // removing them from response to ensure rest of the code works using new store model
        const {
          activationId,
          adapterDownloadUrl,
          externalPosProviderId,
          externalPosRegistrationId,
          integrationType,
          integrationVersion,
          integrationProperties,
          ...store
        } = response;

        delete store.posIntegrationConfigurations.storeNumber;
        return store;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Get adapter download URL for a given storeId
   *
   * @param storeId [string]
   * @returns observable of storeId and adapter download url
   */
  getAdapterDownLoadUrl(storeId: string): Observable<Store> {
    return this.http.get(`${this.storeApiUrl}${storeId}/adapter-download-url`).pipe(
      map((response: Store) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Creates a new store
   *
   * @param newStore [Store]
   * @returns observable of generated store
   */
  create(newStore: Partial<Store>): Observable<Store> {
    const BODY = newStore;
    return this.http.post(this.storeApiUrl, BODY).pipe(
      map((response: Store) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Saves current store
   *
   * @param store [Store]
   * @returns observable of generated store
   */
  save(store: Partial<Store>, storeId: string): Observable<Store> {
    const BODY = store;
    return this.http.put(`${this.storeApiUrl}${storeId}`, BODY).pipe(
      map((response: Store) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateAggregators(storeId: string, aggregators: AggregatorConfig[]): Observable<any> {
    const BODY = {
      aggregators,
    };
    const url = `${this.storeApiUrl}${storeId}/aggregators`;
    return this.http.put(url, BODY).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getMenuJobStatus(storeId: string, jobId: string): Observable<MenuJob> {
    const url = `${this.storeApiUrl}${storeId}/menu-extract/${jobId}`;
    return this.http.get(url).pipe(
      map((response: MenuJob) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all api keys
   *
   * @returns observable of all api keys
   */
  getMarkupOverrides(
    ActiveTenantIdentifier: string,
    storeId: string,
    catalogEntryId: string,
  ): Observable<MarkupOverride[]> {
    let params = { storeId, catalogEntryId, 'Active-Tenant-Identifier': ActiveTenantIdentifier };
    return this.generatedStoresService
      .storesStoreIdCatalogEntryOverridesCatalogEntryIdGet(params)
      .pipe(
        map((response: PriceOverrides) => {
          const embedded = response?._embedded as any;
          if (embedded?.menuPrice) {
            return embedded.menuPrice;
          } else if (embedded?.menuPrices) {
            return embedded.menuPrices;
          } else if (embedded?.priceOverrides) {
            return embedded.priceOverrides;
          } else {
            return [];
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Creates a new user
   *
   * @param newUser [Store]
   * @returns observable of created user
   */
  saveMarkupOverrides(
    ActiveTenantIdentifier,
    storeId,
    markupOverrides: MarkupOverride[],
  ): Observable<MarkupOverride[]> {
    let params: BulkUpdateCatalogEntryOverrides$Params = {
      storeId,
      body: markupOverrides,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.generatedProductsService.bulkUpdateCatalogEntryOverrides(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Deletes price overrides for catalogEntryId on storeId for channel
   * @param params The `StoresService.DeleteStoresStoreIdCatalogEntryOverridesCatalogEntryIdChannelParams` containing the following parameters:
   * - `storeId`:
   * - `channel`:
   * - `catalogEntryId`:
   * - `Active-Tenant-Identifier`:
   * @return Successfully deleted priceOverrides
   */

  deleteMarkupOverrides(
    storeId: string,
    channelId: string,
    catalogEntryId: string,
    ActiveTenantIdentifier: string,
  ): Observable<MarkupOverride[]> {
    let params: StoresStoreIdCatalogEntryOverridesCatalogEntryIdChannelIdDelete$Params = {
      storeId,
      channelId,
      catalogEntryId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.generatedStoresService
      .storesStoreIdCatalogEntryOverridesCatalogEntryIdChannelIdDelete(params)
      .pipe(
        map((response: any) => {
          if (response) {
            return response;
          } else {
            return undefined;
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets store aggregator onboarding
   *
   * @returns observable of store aggregator onboarding
   */
  getStoreAggregatorOnboarding(
    ActiveTenantIdentifier: string,
    storeId: string,
  ): Observable<StoreAggregatorOnboardingGetResponse> {
    let params: StoreAggregatorOnboarding$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.storeAggregatorOnboardingService.storeAggregatorOnboarding(params).pipe(
      map((response: StoreAggregatorOnboardingGetResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets store aggregator onboarding
   *
   * @returns observable of store aggregator onboarding
   */
  updateStoreAggregatorOnboarding(
    params: StoresStoreIdOnboardingAggregatorsAggregatorIdPut$Params,
  ): Observable<StoreAggregatorOnboarding> {
    return this.storeAggregatorOnboardingService
      .storesStoreIdOnboardingAggregatorsAggregatorIdPut(params)
      .pipe(
        map((response: StoreAggregatorOnboarding) => {
          if (response) {
            return response;
          } else {
            return undefined;
          }
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets store aggregator onboarding
   *
   * @returns observable of store aggregator onboarding
   */
  getDoordashValidation(
    ActiveTenantIdentifier: string,
    storeId: string,
  ): Observable<DoordashOnboardingValidationResponse> {
    let params: DoordashStoresStoreIdOnboardingValidationGet$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.doordashService.doordashStoresStoreIdOnboardingValidationGet(params).pipe(
      map((response: DoordashOnboardingValidationResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets store aggregator onboarding
   *
   * @returns observable of store aggregator onboarding
   */
  getDoordashStores(
    ActiveTenantIdentifier: string,
    exchangeCode: string,
  ): Observable<StoresGetResponse> {
    let params: AggregatorsDoordashStoresGet$Params = {
      exchangeCode,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.doordashService.aggregatorsDoordashStoresGet(params).pipe(
      map((response: StoresGetResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets DoorDash Net New Merchant signup URL
   *
   * @returns observable of signup URL response
   */
  getDoordashSignupUrl(
    ActiveTenantIdentifier: string,
    body: SignupUrlRequest,
  ): Observable<SignupUrlResponse> {
    const params: AggregatorsDoordashSignupUrlPost$Params = {
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
      body,
    };
    return this.doordashService.aggregatorsDoordashSignupUrlPost(params).pipe(
      map((response: SignupUrlResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * get store images
   *
   * @param storeId [Store]
   * @returns observable of saved images
   */
  getImages(storeId: string): Observable<ImageItem[]> {
    return this.http.get(`${this.storeApiUrl}${storeId}/images`).pipe(
      map((response: { links: {}; data: ImageItem[] }) => {
        return response.data;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Saves store images
   *
   * @param storeId [Store]
   * @returns observable of saved images
   */
  saveImages(storeId: string, body: ImageItem[]): Observable<ImageItem[]> {
    return this.http.put(`${this.storeApiUrl}${storeId}/images`, body).pipe(
      map((response: { links: {}; data: ImageItem[] }) => {
        return response.data;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets store ordering deactivations
   *
   */
  getStoreOrderingDeactivations(
    ActiveTenantIdentifier: string,
    storeId: string,
  ): Observable<StoreOrderingDeactivation> {
    let params: GetStoreOrderingDeactivations$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
    };
    return this.generatedStoresService.getStoreOrderingDeactivations(params).pipe(
      map((response: StoreOrderingDeactivationsResponse) => {
        if (response) {
          return response as StoreOrderingDeactivation;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets store aggregator summary
   *
   */
  getStoreAggregatorSummary(
    ActiveTenantIdentifier: string,
    storeId: string,
  ): Observable<AggregatorSummaryResponse[]> {
    let params: AggregatorsSummaryGet$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
      size: 100,
    };
    return this.generatedStoresService.aggregatorsSummaryGet(params).pipe(
      map((response: any) => {
        if (response) {
          return response.aggregators;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateStoreOrdering(
    brandId,
    storeId,
    action: 'activate' | 'deactivate' | 'cancel-deactivation',
    body: StoreOrderingStatusRequest,
  ): Observable<any> {
    let params: UpdateStoreOrderingStatus$Params = {
      storeId,
      'Active-Tenant-Identifier': brandId,
      action,
      body,
    };
    return this.generatedStoresService.updateStoreOrderingStatus(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getAmenities(brandId: string, storeId: string): Observable<StoreAmenityResponse[]> {
    let params: GetStoreAmenities$Params = {
      storeId,
      size: 100,
      'Active-Tenant-Identifier': brandId,
    };
    return this.generatedStoresService.getStoreAmenities(params).pipe(
      map((response: StoreAmenityCollectionResponse) => {
        if (response) {
          return response.results;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveAmenities(
    brandId: string,
    storeId: string,
    amenitiesUpdateRequest: UpdateStoreAmenitiesRequest,
  ): Observable<any[]> {
    let params: PutStoreAmenities$Params = {
      storeId,
      'Active-Tenant-Identifier': brandId,
      body: amenitiesUpdateRequest,
    };
    return this.generatedStoresService.putStoreAmenities(params).pipe(
      map((response: UpdateStoreAmenitiesResponse) => {
        if (response) {
          return response.amenities;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateBulkStoresAmenities(brandId: string, body: BulkStoresAmenitiesRequest): Observable<void> {
    let params: BulkStoresAmenities$Params = {
      'Active-Tenant-Identifier': brandId,
      body,
    };
    return this.generatedStoresService.bulkStoresAmenities(params).pipe(
      map(() => {
        return;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getNearbyStores(params: NearbyStoresParams) {
    let httpParams = new ngCommonHttp.HttpParams({
      fromObject: {
        latitude: params.latitude.toString(),
        longitude: params.longitude.toString(),
        ...(params.radius !== undefined && { radius: params.radius.toString() }),
        ...(params.includeDeliveryZones !== undefined && {
          includeDeliveryZones: params.includeDeliveryZones.toString(),
        }),
        ...(params.enabledDeliveryZones !== undefined && {
          enabledDeliveryZones: params.enabledDeliveryZones.toString(),
        }),
        ...(params.deliveryZoneType && { deliveryZoneType: params.deliveryZoneType }),
        ...(params.page !== undefined && { page: params.page.toString() }),
        ...(params.size !== undefined && { size: params.size.toString() }),
      },
    });

    return this.http.get('/tce-experience-admin/stores-nearby', { params: httpParams }).pipe(
      map((response: any) => {
        return response._embedded.store;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getMarkerSvg(color: string, size: number = 20) {
    const svgMarker = encodeURIComponent(`
      <svg width="${size}" height="${size}" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
        <!-- Marker Background -->
        <path d="M12 0C6.477 0 2 4.477 2 10c0 7 10 22 10 22s10-15 10-22c0-5.523-4.477-10-10-10z" fill="${color}" />
        <!-- Inner Circle -->
        <circle cx="12" cy="10" r="4" fill="white" />s
      </svg>
    `);

    return svgMarker;
  }

  /**
   * Marks or unmarks a catalog entry as out-of-stock for a store.
   * Wraps generated AvailabilityService endpoint: PUT /stores/{storeId}/availability/out-of-stock
   */
  setOutOfStock(
    ActiveTenantIdentifier: string,
    storeId: string,
    payload: OutOfStockUpdateInput,
  ): Observable<void> {
    const params: OutOfStock$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
      body: payload,
    };

    return this.availabilityService.outOfStock(params).pipe(
      map(() => {
        return;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Marks or unmarks multiple catalog entries as out-of-stock for a store.
   * Wraps generated AvailabilityService endpoint: PUT /stores/{storeId}/availability/out-of-stock/bulk
   */
  setOutOfStockBulk(
    ActiveTenantIdentifier: string,
    storeId: string,
    payload: OutOfStockUpdateInput[],
  ): Observable<void> {
    const params: OutOfStockBulk$Params = {
      storeId,
      'Active-Tenant-Identifier': ActiveTenantIdentifier,
      body: payload,
    };

    return this.availabilityService.outOfStockBulk(params).pipe(
      map(() => {
        return;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  createOutOfStockBulkJob(
    brandId: string,
    body: ChannelsBulkJobsRequest,
  ): Observable<{ jobId?: string; status?: string }> {
    return this.channelsBulkJobsService
      .channelsBulkJobsPost({
        'Active-Tenant-Identifier': brandId,
        body,
      })
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Gets all store-level dayparts configurations
   *
   * @param brandId Brand ID
   * @param storeId Store ID
   * @returns Observable of DayPartMap
   */
  getStoreDayparts(brandId: string, storeId: string): Observable<DayPartMap> {
    // First, get brand dayparts to know what's available
    return this.http
      .get(`/tce-experience-admin/dayparts`, {
        headers: new HttpHeaders({ 'Active-Tenant-Identifier': brandId }),
      })
      .pipe(
        mergeMap((response: any) => {
          const brandDayparts = response?._embedded?.dayparts || [];

          if (brandDayparts.length === 0) {
            return of({});
          }

          // Create an array of requests to get store-specific configurations
          const storeConfigRequests = brandDayparts.map((brandDaypart: any) => {
            const daypartId = brandDaypart.daypartId;
            if (!daypartId) {
              return of(null);
            }

            const params: DaypartsDaypartIdStoresStoreIdGet$Params = {
              'Active-Tenant-Identifier': brandId,
              daypartId: daypartId,
              storeId: storeId,
            };

            return this.daypartsService.daypartsDaypartIdStoresStoreIdGet(params).pipe(
              map((storeConfig) => ({
                daypartId,
                brandName: brandDaypart.name,
                storeConfig: storeConfig,
              })),
              catchError(() => of(null)), // Store doesn't have this daypart configured (404)
            );
          });

          // Wait for all store configuration requests to complete
          return forkJoin(storeConfigRequests).pipe(
            map(
              (
                results: Array<{
                  daypartId: string;
                  brandName: string;
                  storeConfig: any;
                } | null>,
              ) => {
                const dayPartMap: DayPartMap = {};

                results.forEach((result) => {
                  if (result && result.storeConfig && result.storeConfig.hours) {
                    dayPartMap[result.daypartId] = {
                      id: result.daypartId,
                      name: result.storeConfig.name || result.brandName,
                      hours: result.storeConfig.hours,
                    };
                  }
                });

                return dayPartMap;
              },
            ),
          );
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Saves or updates a store-level daypart configuration
   *
   * @param brandId Brand ID
   * @param storeId Store ID
   * @param daypartId Daypart ID
   * @param hours WeekHours configuration
   * @returns Observable<void>
   */
  saveStoreDaypart(
    brandId: string,
    storeId: string,
    daypartId: string,
    hours: WeekHours,
  ): Observable<void> {
    const params: DaypartsDaypartIdStoresStoreIdPut$Params = {
      'Active-Tenant-Identifier': brandId,
      daypartId: daypartId,
      storeId: storeId,
      body: hours,
    };

    return this.daypartsService
      .daypartsDaypartIdStoresStoreIdPut(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Deletes a store-level daypart configuration
   *
   * @param brandId Brand ID
   * @param storeId Store ID
   * @param daypartId Daypart ID
   * @returns Observable<void>
   */
  deleteStoreDaypart(brandId: string, storeId: string, daypartId: string): Observable<void> {
    // Delete by setting hours to null/empty
    return this.saveStoreDaypart(brandId, storeId, daypartId, {});
  }

  /**
   * Gets all menu-level dayparts configurations
   *
   * @param brandId Brand ID
   * @param menuId Menu ID
   * @returns Observable of DayPartMap
   */
  getMenuDayparts(brandId: string, menuId: string): Observable<DayPartMap> {
    // First, get brand dayparts to know what's available
    return this.http
      .get(`/tce-experience-admin/dayparts`, {
        headers: new HttpHeaders({ 'Active-Tenant-Identifier': brandId }),
      })
      .pipe(
        mergeMap((response: any) => {
          const brandDayparts = response?._embedded?.dayparts || [];

          if (brandDayparts.length === 0) {
            return of({});
          }

          // Create an array of requests to get menu-specific configurations
          const menuConfigRequests = brandDayparts.map((brandDaypart: any) => {
            const daypartId = brandDaypart.daypartId;
            if (!daypartId) {
              return of(null);
            }

            const params: DaypartsDaypartIdMenusMenuIdGet$Params = {
              'Active-Tenant-Identifier': brandId,
              daypartId: daypartId,
              menuId: menuId,
            };

            return this.daypartsService.daypartsDaypartIdMenusMenuIdGet(params).pipe(
              map((menuConfig) => ({
                daypartId,
                brandName: brandDaypart.name,
                menuConfig: menuConfig,
              })),
              catchError(() => of(null)), // Menu doesn't have this daypart configured (404)
            );
          });

          // Wait for all menu configuration requests to complete
          return forkJoin(menuConfigRequests).pipe(
            map(
              (
                results: Array<{
                  daypartId: string;
                  brandName: string;
                  menuConfig: any;
                } | null>,
              ) => {
                const dayPartMap: DayPartMap = {};

                results.forEach((result) => {
                  if (result && result.menuConfig && result.menuConfig.hours) {
                    dayPartMap[result.daypartId] = {
                      id: result.daypartId,
                      name: result.menuConfig.name || result.brandName,
                      hours: result.menuConfig.hours,
                    };
                  }
                });

                return dayPartMap;
              },
            ),
          );
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Saves or updates a menu-level daypart configuration
   *
   * @param brandId Brand ID
   * @param menuId Menu ID
   * @param daypartId Daypart ID
   * @param hours WeekHours configuration
   * @returns Observable<void>
   */
  saveMenuDaypart(
    brandId: string,
    menuId: string,
    daypartId: string,
    hours: WeekHours,
  ): Observable<void> {
    const params: DaypartsDaypartIdMenusMenuIdPut$Params = {
      'Active-Tenant-Identifier': brandId,
      daypartId: daypartId,
      menuId: menuId,
      body: hours,
    };

    return this.daypartsService
      .daypartsDaypartIdMenusMenuIdPut(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Deletes a menu-level daypart configuration
   *
   * @param brandId Brand ID
   * @param menuId Menu ID
   * @param daypartId Daypart ID
   * @returns Observable<void>
   */
  deleteMenuDaypart(brandId: string, menuId: string, daypartId: string): Observable<void> {
    // Delete by setting hours to null/empty
    return this.saveMenuDaypart(brandId, menuId, daypartId, {});
  }
}
